// Pure parts of the suite's own context menu (no DOM, no React): entry model, cleanup, placement and keyboard moves.
// The host (ContextMenu.tsx) renders; providers (registry.ts, text.ts and each program's own) describe what a right-click offers.

/** Name of an icon of the base set (icons.tsx) or one a program added with `registerCtxIcons`. */
export type CtxIcon = string;

export interface CtxAction {
  /** Visible label, already in the UI language (the program translates). */
  label: string;
  run: () => unknown;
  icon?: CtxIcon;
  /** Right-aligned hint, e.g. a shortcut („⌘C“) or a value preview. */
  hint?: string;
  disabled?: boolean;
  danger?: boolean;
}
export interface CtxHeader { header: string }
/** An entry that opens a submenu to its right on hover / → (like macOS and Windows), e.g. „Status ▸“. */
export interface CtxSub {
  label: string;
  sub: CtxEntry[];
  icon?: CtxIcon;
  /** Shown before the arrow, e.g. the current status. */
  hint?: string;
  disabled?: boolean;
}
export type CtxEntry = CtxAction | CtxSub | CtxHeader | 'sep';

export const isAction = (e: CtxEntry | undefined): e is CtxAction => !!e && typeof e === 'object' && 'run' in e;
export const isSub = (e: CtxEntry | undefined): e is CtxSub => !!e && typeof e === 'object' && 'sub' in e;
/** Entries the pointer/keyboard can land on (actions and submenus). */
export const isItem = (e: CtxEntry | undefined): e is CtxAction | CtxSub => isAction(e) || isSub(e);

/** Drops empty groups: no separator at the start/end, never two in a row, headers only when something follows. */
export function tidy(entries: Array<CtxEntry | null | undefined | false>): CtxEntry[] {
  const out: CtxEntry[] = [];
  for (const e of entries) {
    if (!e) continue;
    // A submenu without anything to choose is left out.
    if (isSub(e)) { const sub = tidy(e.sub); if (sub.some(isItem)) out.push({ ...e, sub }); continue; }
    if (e === 'sep') { if (out.length && out[out.length - 1] !== 'sep') out.push(e); continue; }
    out.push(e);
  }
  // A header followed by a separator (or nothing) has no entries: remove it, then re-tidy separators.
  const res: CtxEntry[] = [];
  for (let i = 0; i < out.length; i++) {
    const e = out[i]!;
    if (typeof e === 'object' && 'header' in e && !isItem(out[i + 1])) continue;
    if (e === 'sep' && (res.length === 0 || res[res.length - 1] === 'sep')) continue;
    res.push(e);
  }
  while (res.length && (res[res.length - 1] === 'sep')) res.pop();
  return res;
}

/** Joins groups with separators (empty groups vanish). */
export const groups = (...gs: Array<Array<CtxEntry | null | undefined | false> | null | undefined>) =>
  tidy(gs.filter(Boolean).flatMap((g, i) => (i ? ['sep' as const, ...g!] : g!)));

/**
 * Where the menu goes for a click at (x, y): right/below the pointer like native menus, flipped to the left/up when
 * it would leave the viewport, and always kept `margin` px inside (a menu taller than the window starts at the top).
 */
export function place(x: number, y: number, w: number, h: number, vw: number, vh: number, margin = 6): { left: number; top: number } {
  let left = x + 2, top = y + 2;
  if (left + w > vw - margin) left = x - w - 2;
  if (top + h > vh - margin) top = y - h - 2;
  left = Math.max(margin, Math.min(left, vw - w - margin));
  top = Math.max(margin, Math.min(top, vh - h - margin));
  return { left, top };
}

const usable = (e: CtxEntry | undefined) => isItem(e) && !e.disabled;

/** Next enabled entry from `cur` in direction `dir` (wraps; -1 = nothing active yet). Returns -1 when none is enabled. */
export function step(entries: CtxEntry[], cur: number, dir: 1 | -1): number {
  const n = entries.length;
  if (!n) return -1;
  let i = cur < 0 ? (dir === 1 ? -1 : n) : cur;
  for (let k = 0; k < n; k++) {
    i = (i + dir + n) % n;
    if (usable(entries[i])) return i;
  }
  return -1;
}
export const first = (entries: CtxEntry[]) => step(entries, -1, 1);
export const last = (entries: CtxEntry[]) => step(entries, -1, -1);

/** Type-ahead: next enabled entry (after `cur`) whose label starts with `ch`. */
export function byLetter(entries: CtxEntry[], cur: number, ch: string): number {
  const c = ch.toLowerCase(), n = entries.length;
  for (let k = 1; k <= n; k++) {
    const i = (cur + k + n) % n, e = entries[i];
    if (usable(e) && (e as CtxAction | CtxSub).label.trim().toLowerCase().startsWith(c)) return i;
  }
  return cur;
}

export type MenuKey = { kind: 'move'; to: number } | { kind: 'run'; index: number } | { kind: 'open'; index: number } | { kind: 'close' } | { kind: 'none' };

/**
 * Keyboard handling of an open menu: ↑ ↓ Home End move, Enter/Space run (or open a submenu), → opens a submenu,
 * ← / Esc close (in a submenu: back to its parent), Tab closes, letters jump.
 */
export function onKey(entries: CtxEntry[], cur: number, key: string): MenuKey {
  if ((key === 'ArrowRight' || key === 'Enter' || key === ' ') && isSub(entries[cur]) && usable(entries[cur])) return { kind: 'open', index: cur };
  switch (key) {
    case 'ArrowDown': return { kind: 'move', to: step(entries, cur, 1) };
    case 'ArrowUp': return { kind: 'move', to: step(entries, cur, -1) };
    case 'Home': case 'PageUp': return { kind: 'move', to: first(entries) };
    case 'End': case 'PageDown': return { kind: 'move', to: last(entries) };
    case 'Enter': case ' ': return usable(entries[cur]) ? { kind: 'run', index: cur } : { kind: 'none' };
    case 'Escape': case 'Tab': case 'ArrowLeft': return { kind: 'close' };
    default:
      if (key.length === 1 && /\S/.test(key)) { const to = byLetter(entries, cur, key); return to !== cur ? { kind: 'move', to } : { kind: 'none' }; }
      return { kind: 'none' };
  }
}

/**
 * Where a submenu goes: to the right of its row (overlapping 2 px like native menus), flipped to the left of the parent
 * when it would leave the viewport; vertically aligned with the row and kept inside.
 */
export function placeSub(row: { left: number; right: number; top: number }, w: number, h: number, vw: number, vh: number, margin = 6): { left: number; top: number } {
  // The row sits 4 px (padding) + 1 px (border) inside its menu: start 3 px right of the row = 2 px over the menu's edge.
  let left = row.right + 3;
  if (left + w > vw - margin) left = row.left - w - 3;
  let top = row.top - 5;
  if (top + h > vh - margin) top = vh - h - margin;
  return { left: Math.max(margin, left), top: Math.max(margin, top) };
}

/** Mac shows ⌘, Windows/Linux Ctrl+. */
export const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const shortcut = (k: string, shift = false) => (isMac() ? (shift ? '⇧⌘' : '⌘') + k : 'Ctrl+' + (shift ? 'Shift+' : '') + k);
